import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  signUp,
  confirmSignUp,
  autoSignIn,
  signOut,
  signIn,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  resendSignUpCode,
  updatePassword,
} from "aws-amplify/auth";
import {
  notAuthenticated,
  onLoading,
  onErrorMessage,
  onLogin,
  onChecking,
  onLogout,
  onConfirmCode,
  onLoginMessage,
  onUpdateUser,
  updateDoctor,
} from "../../store/auth";
import {
  setIsLoading,
  setErrorMessage,
  setSuccessMessage,
} from "../../store/doctor/formEventSlice";
import { gosafeApi } from "../../api";
import { sleep, translations } from "../../helpers";

export const useAuthStore = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, nextStep, message, isLoading, user, rol } = useSelector(
    (state) => state.auth
  );

  const checkAuthToken = useCallback(async () => {
    try {
      dispatch(onChecking());

      // Verificar si hay tokens en localStorage (magic login)
      const storedToken = localStorage.getItem("token");
      const storedRefreshToken = localStorage.getItem("refreshToken");

      if (storedToken) {
        try {
          // Intentar usar el token actual
          const { data } = await gosafeApi.get("user");
          dispatch(onLogin(data));
          return;
        } catch (error) {
          // Si el token expiró (401), intentar refresh
          if (error?.response?.status === 401 && storedRefreshToken) {
            try {
              const { data } = await gosafeApi.post("refresh-token", {
                refreshToken: storedRefreshToken,
              });

              const { AuthenticationResult } = data;

              // Guardar nuevos tokens
              localStorage.setItem("token", AuthenticationResult.IdToken);
              localStorage.setItem(
                "accessToken",
                AuthenticationResult.AccessToken
              );
              localStorage.setItem(
                "refreshToken",
                AuthenticationResult.RefreshToken
              );

              // Reintentar la petición original
              const { data: userData } = await gosafeApi.get("user");
              dispatch(onLogin(userData));
              return;
            } catch (refreshError) {
              // Refresh falló, continuar con login normal
              localStorage.removeItem("token");
              localStorage.removeItem("accessToken");
              localStorage.removeItem("refreshToken");
            }
          } else {
            // Error diferente a 401, limpiar tokens
            localStorage.removeItem("token");
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
          }
        }
      }

      // Si no hay tokens guardados o refresh falló, usar fetchAuthSession (login normal)
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      localStorage.setItem("token", idToken.toString());
      const { data } = await gosafeApi.get("user");
      dispatch(onLogin(data));
    } catch (error) {
      localStorage.removeItem("token");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      dispatch(
        notAuthenticated(
          translations[error?.response?.data?.message] ||
            error?.response?.data?.message ||
            error?.message
        )
      );
      return;
    }
  }, [dispatch, fetchAuthSession]);

  const startLogin = useCallback(
    async ({ email: username, password }) => {
      try {
        dispatch(onLoading());
        const { isSignedIn, nextStep } = await signIn({ username, password });
        if (isSignedIn && nextStep.signInStep === "DONE") {
          await checkAuthToken();
          return;
        }
        if (!isSignedIn && nextStep.signInStep === "CONFIRM_SIGN_UP") {
          dispatch(onErrorMessage("Debes activar tu cuenta"));
          return;
        }
      } catch (error) {
        dispatch(
          onErrorMessage(translations[error?.message] || error?.message)
        );
        return;
      }
    },
    [dispatch, checkAuthToken]
  );

  const startMagicLogin = useCallback(
    async (encryptedKey) => {
      try {
        dispatch(onLoading());

        const body = {
          key: encryptedKey,
        };

        // Enviar key encriptada al backend para obtener tokens
        const { data } = await gosafeApi.post("/magic-login", body);

        const { AuthenticationResult } = data;

        // console.log("data", data);

        localStorage.setItem("token", AuthenticationResult.IdToken);
        localStorage.setItem("accessToken", AuthenticationResult.AccessToken);
        localStorage.setItem("refreshToken", AuthenticationResult.RefreshToken);

        const { data: userData } = await gosafeApi.get("user");

        // console.log("userData", userData);

        dispatch(onLogin(userData));
        navigate("/");
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
        navigate("/auth/login");
        return;
      }
    },
    [dispatch]
  );

  const startLogout = useCallback(async () => {
    try {
      dispatch(onChecking());

      const hasAmplifySession =
        localStorage.getItem("token") && !localStorage.getItem("magicLogin");

      if (hasAmplifySession) {
        await signOut();
      }

      localStorage.removeItem("token");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("magicLogin");

      dispatch(onLogout());
      return;
    } catch (error) {
      console.log("error signing out: ", error);
    }
  }, [dispatch, signOut]);

  const startRegister = useCallback(
    async ({
      document,
      documentTypeId,
      firstName,
      lastName,
      email,
      phoneNumber,
      countryPhoneId,
      country,
      department,
      city,
      address,
      genderId,
      ipsId,
      password,
      confirmPassword,
    }) => {
      try {
        dispatch(onLoading());

        if (password !== confirmPassword) {
          return dispatch(onErrorMessage("Las contraseñas no coinciden"));
        }

        const { isSignUpComplete, userId, nextStep } = await signUp({
          username: email,
          password,
          options: {
            autoSignIn: true,
          },
        });

        const userAttributesApi = {
          id: userId,
          firstName,
          lastName,
          email,
          phoneNumber,
          country,
          document,
          department,
          city,
          address,
          documentTypeId,
          countryPhoneId,
          genderId,
          ipsId,
        };

        const { data } = await gosafeApi.post("admin", userAttributesApi);

        if (!isSignUpComplete && nextStep.signUpStep === "CONFIRM_SIGN_UP") {
          dispatch(onConfirmCode(data));
          return;
        }
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              translations[error?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
        return;
      }
    },
    [dispatch, signUp]
  );

  const startAutoLogin = useCallback(async () => {
    try {
      const { isSignedIn, nextStep } = await autoSignIn();
      if (isSignedIn && nextStep.signInStep === "DONE") {
        await checkAuthToken();
        return;
      }
    } catch (error) {
      console.log(error);
    }
  }, [autoSignIn, checkAuthToken]);

  const startConfirmCode = useCallback(
    async ({ code: confirmationCode }) => {
      try {
        dispatch(onLoading());

        const { isSignUpComplete, nextStep } = await confirmSignUp({
          username: user.email,
          confirmationCode,
        });

        const { data } = await gosafeApi.put(`admin//activate`, {
          email: user.email,
        });

        if (isSignUpComplete) {
          switch (nextStep.signUpStep) {
            case "COMPLETE_AUTO_SIGN_IN":
              await startAutoLogin();
              return;
            case "DONE":
              navigate("/auth/login");
              dispatch(
                onLoginMessage("Ya puedes ingresar con tus credenciales")
              );
              return;
            default:
              return dispatch(onErrorMessage("Error"));
          }
        }
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              translations[error?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
        return;
      }
    },
    [dispatch, confirmSignUp, startAutoLogin, navigate]
  );

  const startResendSignUpCode = useCallback(
    async ({ email: username }) => {
      try {
        dispatch(onLoading());

        await resendSignUpCode({ username });

        dispatch(onConfirmCode({ email: username }));
        return;
      } catch (error) {
        return dispatch(
          onErrorMessage(translations[error?.message] || error?.message)
        );
      }
    },
    [dispatch, resendSignUpCode]
  );

  const startResetPassword = useCallback(
    async ({ email: username }) => {
      try {
        dispatch(onLoading());

        const { isPasswordReset, nextStep } = await resetPassword({ username });
        if (
          !isPasswordReset &&
          nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE"
        ) {
          dispatch(onConfirmCode({ email: username }));
          return;
        }
      } catch (error) {
        dispatch(
          onErrorMessage(translations[error?.message] || error?.message)
        );
        return;
      }
    },
    [dispatch, resetPassword]
  );

  const startConfirmResetPassword = useCallback(
    async ({
      code: confirmationCode,
      password: newPassword,
      confirmPassword,
    }) => {
      try {
        dispatch(onLoading());

        if (newPassword !== confirmPassword) {
          return dispatch(onErrorMessage("Las contraseñas no coinciden"));
        }

        await confirmResetPassword({
          username: user.email,
          confirmationCode,
          newPassword,
        });

        navigate("/auth/login");
        dispatch(onLoginMessage("Ya puedes ingresar con tus credenciales"));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(translations[error?.message] || error?.message)
        );
        return;
      }
    },
    [dispatch, confirmResetPassword, navigate]
  );

  const startUpdateDoctor = useCallback(
    async (body) => {
      try {
        dispatch(setIsLoading());

        // console.log("body", body);
        // await sleep(2000);
        // const data = {
        //   message: "Muy bien",
        //   doctor: body,
        // };

        const { data } = await gosafeApi.put(`doctor/updateProfile`, body);

        dispatch(updateDoctor(data));
        dispatch(setSuccessMessage(translations[data.message] || data.message));
      } catch (error) {
        dispatch(
          setErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch]
  );

  const startUpdatePasswordDoctor = useCallback(
    async ({ oldPassword, newPassword, confirmPassword }) => {
      try {
        dispatch(setIsLoading());

        if (newPassword !== confirmPassword) {
          return dispatch(setErrorMessage("Las nuevas claves deben coincidir"));
        }

        await updatePassword({ oldPassword, newPassword });

        dispatch(setSuccessMessage("La clave ha sido actualizada con exito"));
      } catch (error) {
        dispatch(
          setErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch]
  );

  return {
    // Props
    status,
    user,
    rol,
    nextStep,
    isLoading,
    message,

    // Methods
    checkAuthToken,
    startRegister,
    startConfirmCode,
    startLogout,
    startLogin,
    startMagicLogin,
    startResetPassword,
    startConfirmResetPassword,
    startResendSignUpCode,
    startUpdateDoctor,
    startUpdatePasswordDoctor,
  };
};
